import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SectionTitle from "../components/SectionTitle";
import Counter from "../components/Counter";
import { TIMELINE, STATS, COMPANY } from "../mock/mock";
import { imageUrl } from "../lib/api";
import { Plus, Minus, Facebook, Instagram } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const HALL_OF_FAME = [
  { img: "/halloffame/car3.jpg", brand: "Ferrari", model: "F8 Tributo", name: "Ferrari F8 Tributo" },
  { img: "/halloffame/car4.jpg", brand: "Lamborghini", model: "Urus", name: "Lamborghini Urus" },
  { img: "/halloffame/car5.jpg", brand: "Porsche", model: "911 Cabriolet", name: "Porsche 911 Cabriolet" },
  { img: "/halloffame/car6.jpg", brand: "Porsche", model: "911", name: "Porsche 911" },
  { img: "/halloffame/car2.jpg", brand: "Aston Martin", model: "DBX707", name: "Aston Martin DBX707" },
  { img: "/halloffame/car1.jpg", brand: "Aston Martin", model: "DBX", name: "Aston Martin DBX" },
];

export default function About() {
  const { t } = useI18n();
  const [open, setOpen] = useState(0);
  // Pair translated text content with images from mock data (1:1 by index).
  const tlImages = TIMELINE.map((x) => x.image);
  const timeline = (t("about.timeline") || []).map((x, i) => ({ ...x, image: tlImages[i] }));
  const faqs = t("about.faqs") || [];

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />

      <section className="pt-[140px] pb-16 px-5 lg:px-8">
        <div className="max-w-[1100px] mx-auto text-center">
          <div className="text-[#c5a76f] text-sm tracking-[0.3em] uppercase mb-4">{t("about.overline")}</div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("about.title")} <span className="text-[#c5a76f]">{t("about.titleAccent")}</span>
          </h1>
          <p className="text-white/75 text-lg leading-relaxed max-w-3xl mx-auto">
            {t("about.intro")}
          </p>
          <blockquote className="mt-12 text-xl md:text-2xl text-[#c5a76f] italic" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("about.quote")}
          </blockquote>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-24">
        <div className="max-w-[1100px] mx-auto text-white/75 leading-relaxed space-y-5">
          <p>{t("about.storyP1")}</p>
          <p>{t("about.storyP2")}</p>
          <div className="flex gap-3 pt-4">
            <a href={COMPANY.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-[#c5a76f]/40 flex items-center justify-center text-[#c5a76f] hover:bg-[#c5a76f] hover:text-black transition-colors">
              <Facebook className="w-4 h-4" />
            </a>
            <a href={COMPANY.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-[#c5a76f]/40 flex items-center justify-center text-[#c5a76f] hover:bg-[#c5a76f] hover:text-black transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-5 lg:px-8 py-20 bg-[#141414] border-y border-white/5">
        <div className="max-w-[1200px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[
            { val: STATS.years, label: t("about.statsYears") },
            { val: STATS.carsOnRequest, label: t("about.statsOnRequest") },
            { val: STATS.carsSold, label: t("about.statsSold") },
            { val: STATS.importsOnRequest, label: t("about.statsImports") },
          ].map((s) => (
            <div key={s.label}>
              <div className="text-5xl md:text-6xl font-bold text-[#c5a76f] mb-2" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
                <Counter to={s.val} suffix={s.val >= 100 ? "+" : ""} />
              </div>
              <div className="text-white/70 text-sm tracking-wider uppercase">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Hall of Fame */}
      <section className="px-5 lg:px-8 py-24">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <div className="text-[#c5a76f] text-sm tracking-[0.3em] uppercase mb-3">{t("about.hofOverline")}</div>
            <h2 className="text-3xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
              {t("about.hofTitle")} <span className="text-[#c5a76f]">{t("about.hofTitleAccent")}</span>
            </h2>
            <p className="text-white/65 leading-relaxed max-w-2xl mx-auto mt-5">{t("about.hofIntro")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="hall-of-fame-grid">
            {HALL_OF_FAME.map((car, i) => (
              <div
                key={car.img}
                data-testid={`hof-card-${i}`}
                className={`group relative overflow-hidden rounded-lg border border-white/10 hover:border-[#c5a76f]/50 transition-all duration-300 ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}
              >
                <img
                  src={car.img}
                  alt={car.name}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i === 0 ? "aspect-[16/11] lg:h-full" : "aspect-[16/10]"}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="text-[#c5a76f] text-[11px] tracking-[0.25em] uppercase mb-1">{car.brand}</div>
                  <div className="text-white font-bold leading-tight" style={{ fontFamily: "'Familjen Grotesk', serif", fontSize: i === 0 ? "1.6rem" : "1.15rem" }}>
                    {car.model}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="px-5 lg:px-8 py-24">
        <div className="max-w-[1200px] mx-auto">
          <SectionTitle overline={t("about.timelineOverline")} title={t("about.timelineTitle")} />
          <div className="relative">
            <div className="absolute left-4 lg:left-1/2 lg:-translate-x-px top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#c5a76f]/40 to-transparent" />
            {timeline.map((tl, i) => (
              <div key={tl.year} className={`relative mb-12 lg:mb-20 grid lg:grid-cols-2 gap-8 items-center ${i % 2 === 0 ? "" : "lg:flex-row-reverse"}`}>
                <div className="absolute left-4 lg:left-1/2 lg:-translate-x-1/2 w-3 h-3 rounded-full bg-[#c5a76f] ring-4 ring-[#c5a76f]/20" />
                <div className={`pl-12 lg:pl-0 ${i % 2 === 0 ? "lg:pr-16 lg:text-right" : "lg:order-2 lg:pl-16"}`}>
                  <div className="text-[#c5a76f] text-3xl font-bold mb-2" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
                    {tl.year}
                  </div>
                  <h3 className="text-white text-xl font-bold mb-3">{tl.title}</h3>
                  <p className="text-white/65 leading-relaxed">{tl.text}</p>
                </div>
                <div className={`pl-12 lg:pl-0 ${i % 2 === 0 ? "lg:order-2" : ""}`}>
                  <div className="rounded-md overflow-hidden border border-white/10">
                    <img src={imageUrl(tl.image)} alt={tl.title} className="w-full aspect-[16/10] object-cover" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-5 lg:px-8 py-24 bg-[#141414]">
        <div className="max-w-[900px] mx-auto">
          <SectionTitle overline={t("about.faqOverline")} title={t("about.faqTitle")} />
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-[#242424] border border-white/5 rounded-md overflow-hidden">
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <span className="text-white font-medium pr-4">{f.q}</span>
                  {open === i ? <Minus className="w-5 h-5 text-[#c5a76f] flex-shrink-0" /> : <Plus className="w-5 h-5 text-[#c5a76f] flex-shrink-0" />}
                </button>
                <div className={`grid transition-all duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <div className="px-6 pb-5 text-white/70 leading-relaxed">{f.a}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
